import { createSelector } from 'reselect';
import { ErrCode, FileItem, LinkListItem, MultiAdminUserData } from '../shared/models';
import { ActionReducer, ActionReducerMap } from '@ngrx/store';

import * as fromAuth from './auth.reducer';
import * as fromCore from './core.reducer';
import * as fromFileList from './file-list.reducer';
import * as fromLinkFileList from './link-file-list.reducer';
import * as fromLinkList from './link-list.reducer';
import * as fromUserList from './user-list.reducer';
import * as fromJobStatus from './job-status.reducer';
import * as fromChargebee from './chargebee.reducer';
import * as fromProvision from './provision.reducer';
import * as fromWebAuth from './webauth.reducer';

import { localStorageSync } from 'ngrx-store-localstorage';

import { MetaReducer } from '@ngrx/store';

import { User } from '../shared/models';

export interface State {
  data: any;
  auth: fromAuth.State;
  core: fromCore.State;
  filelist: fromFileList.State;
  linkfilelist: fromLinkFileList.State;
  jobstatus: fromJobStatus.JobState;
  linklist: fromLinkList.State;
  userlist: fromUserList.State;
  chargebee: fromChargebee.State;
  provision: fromProvision.State;
  webAuth: fromWebAuth.State;
}

export const reducers: ActionReducerMap<State> = {
  auth: fromAuth.reducer,
  core: fromCore.reducer,
  filelist: fromFileList.reducer,
  linkfilelist: fromLinkFileList.reducer,
  jobstatus: fromJobStatus.jobReducer,
  linklist: fromLinkList.reducer,
  userlist: fromUserList.reducer,
  chargebee: fromChargebee.reducer,
  data: undefined,
  provision: fromProvision.reducer,
  webAuth: fromWebAuth.reducer,
};

/**
 * Every reducer module exports selector functions, however child reducers
 * have no knowledge of the overall state tree. To make them useable, we
 * need to make new selectors that wrap them.
 *
 * The createSelector function from the reselect library creates
 * very efficient selectors that are memoized and only recompute when arguments change.
 * The created selectors can also be composed together to select different
 * pieces of state.
 */
export const getAuthState = (state: State): fromAuth.State => state.auth;
export const getAuthUser = createSelector(getAuthState, fromAuth.getUser);
export const getAuthenticated = createSelector(getAuthState, fromAuth.getAuthenticated);
export const getAuthLoginProgress = createSelector(getAuthState, fromAuth.getLoginProgress);
export const getAuthError = createSelector(getAuthState, fromAuth.getErrCode);

export const getCoreState = (state: State): fromCore.State => state.core;
export const getSignApiData = createSelector(getAuthUser, getCoreState, (user: User, core: fromCore.State) =>
  Object.assign({}, user, core),
);
export const getAccessToken = (state: State): string => state.core.access_token;

export const getFileListState = (state: State): fromFileList.State => state.filelist;

export const getFavoritsListState = (state: State): fromFileList.State => state.filelist;
export const getLoadPer = (state: State): fromJobStatus.JobState => state.jobstatus;

export const getFileListCwd = (state: State): FileItem => state.filelist.cwd;

export const getFileListItems = (state: State): FileItem[] => state.filelist.pathlist;
export const fileSelector = createSelector(getFileListItems, (state) => state);

export const getTotalStarredCount = createSelector(getFileListState, (state) => state.total_favorite ?? 0);

export const getSyncId = (state: State): number => state.filelist.sync_id;
export const syncIdSelector = createSelector(getSyncId, (syncId) => syncId);
export const getFileListError = (state: State): ErrCode => state.filelist.error;
export const getFileListShowDeleted = (state: State): boolean => state.filelist.showdeleted;
export const getFileListDeletedAmt = (state: State): number => state.filelist.deletedAmt;

export const getFileListSelected = (state: State): FileItem[] =>
  state.filelist.pathlist.filter((item) => item.active == true);
export const getUserListSelected = (state: State): MultiAdminUserData[] =>
  state.userlist.list.filter((item) => item.active == true);

export const getLinkFileListState = (state: State): fromLinkFileList.State => state.linkfilelist;
export const getLinkFileListCwd = (state: State): FileItem => state.linkfilelist.cwd;

export const getIframeReady = (state: State): boolean => state.linkfilelist.iframeReady;

export const getLinkListState = (state: State): fromLinkList.State => state.linklist;
export const getLinkListItems = (state: State): LinkListItem[] => state.linklist.items;
export const getChargebeeState = (state: State): fromChargebee.State => state.chargebee;
export const getProvisionState = (state: State): fromProvision.State => state.provision;

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({
    keys: ['core', 'auth', 'provision', 'webAuth'],
    rehydrate: true,
    storageKeySerializer: (key) => 'sync_' + key,
  })(reducer);
}
export const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

export const getWebAuthState = (state: State): fromWebAuth.State => state.webAuth;
